/**
 * Google Analytics 4 page tracking.
 *
 * Off unless `VITE_GA_MEASUREMENT_ID` is set at build time, so local dev and any
 * build without an id never load gtag.js. The app is a SPA — routes change via
 * the History API without a page load — so page views are sent by
 * `AnalyticsPageTracker` on every React Router location change rather than by
 * gtag's automatic page_view. Turn OFF "Page changes based on browser history
 * events" in the GA web stream's enhanced measurement, or every navigation is
 * counted twice.
 *
 * What reaches Google is deliberately narrow: the path only (no query string, no
 * fragment), and no page view at all for the auth-callback route — /sync-session
 * receives the access token in its fragment or query. No user id is sent.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;

/** Routes that carry credentials, are dev-only, or only redirect. */
const UNTRACKED_PATHS = new Set(['/', '/sync-session', '/dev-auth-helper']);

const SECTION_TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  assets: 'Asset Gallery',
  collections: 'Collection',
  settings: 'Settings',
};

let activeId: string | null = null;
let lastTrackedLocation: string | null = null;

export interface PageView {
  page_location: string;
  page_title: string;
}

/** The configured measurement id, or null when analytics should stay off. */
export function getMeasurementId(): string | null {
  const raw = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
  if (!raw) return null;
  if (!MEASUREMENT_ID_PATTERN.test(raw)) {
    console.warn(`Ignoring malformed VITE_GA_MEASUREMENT_ID "${raw}" — expected G-XXXXXXXXXX.`);
    return null;
  }
  return raw;
}

function humanize(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}

/** A readable title per route — `document.title` is the same on every page. */
export function titleForPath(pathname: string): string {
  if (pathname === '/settings/keys') return 'API Keys';
  const [section, ...rest] = pathname.split('/').filter(Boolean);
  if (!section) return 'Coral Creator';
  // /labs/image-lab/product-photography → "Image Lab / Product Photography"
  if (section === 'labs') return rest.map(humanize).join(' / ') || 'Labs';
  return SECTION_TITLES[section] ?? humanize(section);
}

/** The page view to report for a path, or null when the route must not be reported. */
export function pageViewFor(
  pathname: string,
  origin: string = window.location.origin,
): PageView | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (UNTRACKED_PATHS.has(path)) return null;
  return { page_location: origin + path, page_title: titleForPath(path) };
}

/** Loads gtag.js and configures the stream. Safe to call more than once. */
export function initAnalytics(): void {
  if (activeId) return;
  const id = getMeasurementId();
  if (!id) return;

  window.dataLayer = window.dataLayer || [];
  // gtag.js expects the `arguments` object itself on the dataLayer; a rest-param
  // array is not equivalent, so this has to stay a plain function.
  function gtag(..._args: unknown[]): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  // Every event carries this location until the tracker replaces it, so nothing
  // gtag sends on its own (session_start, scroll, …) can pick up a token from
  // the address bar.
  gtag('set', { page_location: window.location.origin + window.location.pathname });
  gtag('config', id, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  activeId = id;
}

/** Reports a page view for `pathname`. No-op when analytics is off. */
export function trackPageView(pathname: string): void {
  if (!activeId || !window.gtag) return;
  const view = pageViewFor(pathname);
  // Skipping a repeat of the last location absorbs StrictMode's double effect.
  if (!view || view.page_location === lastTrackedLocation) return;
  lastTrackedLocation = view.page_location;
  window.gtag('set', view);
  window.gtag('event', 'page_view', view);
}
